import { useState, useEffect } from 'react';
import { BarChart2, Shield, RefreshCw } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid, LineChart, Line } from 'recharts';
import { getAgencyAnalytics, getAgentProduction } from '../api.js';

// Agency-wide analytics module — OWNER ONLY, Agency/Enterprise plan. Aggregated
// numbers across all agents (no client names — respects the private-books wall).
// Phase 1: filter bar + Overview tab. Growth/Financial/Agents come in later phases.

const TYPE_LABELS = {
  HEALTH: { es: 'Salud', en: 'Health' }, LIFE: { es: 'Vida', en: 'Life' },
  AUTO: { es: 'Auto', en: 'Auto' }, HOME: { es: 'Hogar', en: 'Home' },
  RENTERS: { es: 'Inquilinos', en: 'Renters' }, FLOOD: { es: 'Inundación', en: 'Flood' },
  DENTAL: { es: 'Dental', en: 'Dental' }, VISION: { es: 'Visión', en: 'Vision' },
  DISABILITY: { es: 'Incapacidad', en: 'Disability' }, LONG_TERM_CARE: { es: 'Cuidado LP', en: 'Long-term Care' },
  MEDICARE_SUPPLEMENT: { es: 'Medicare Supl.', en: 'Medicare Supp.' }, MEDICARE_ADVANTAGE: { es: 'Medicare Adv.', en: 'Medicare Adv.' },
  ANNUITY: { es: 'Anualidad', en: 'Annuity' }, UMBRELLA: { es: 'Paraguas', en: 'Umbrella' },
  COMMERCIAL: { es: 'Comercial', en: 'Commercial' }, WORKERS_COMP: { es: 'Comp. Trab.', en: "Workers' Comp" },
  PET: { es: 'Mascotas', en: 'Pet' }, TRAVEL: { es: 'Viaje', en: 'Travel' },
};
const CHART_COLORS = ['#00d4ff', '#22d3ee', '#a78bfa', '#f472b6', '#fbbf24', '#34d399', '#fb923c', '#60a5fa', '#f87171', '#c084fc'];

function periodRange(preset) {
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth();
  if (preset === 'month') return { from: new Date(y, m, 1).toISOString() };
  if (preset === 'year') return { from: new Date(y, 0, 1).toISOString() };
  if (preset === '12m') return { from: new Date(y, m - 11, 1).toISOString() };
  return {}; // all time
}

export function AgencyAnalyticsView({ lang }) {
  const es = lang === 'es';
  const [tab, setTab] = useState('overview');
  const [period, setPeriod] = useState('year');
  const [agentId, setAgentId] = useState('');
  const [policyType, setPolicyType] = useState('');
  const [carrier, setCarrier] = useState('');
  const [data, setData] = useState(null);
  const [production, setProduction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Per-agent production table (Agents tab) — doesn't depend on filters, fetch once.
  useEffect(() => { getAgentProduction().then(setProduction).catch(() => {}); }, []);

  const load = async () => {
    setLoading(true); setError('');
    try {
      const d = await getAgencyAnalytics({ ...periodRange(period), agentId, policyType, carrier });
      setData(d);
    } catch (err) {
      setError(err.code === 'PLAN_REQUIRED'
        ? (es ? 'Este módulo es exclusivo para planes Agencia o Enterprise.' : 'This module is for Agency or Enterprise plans.')
        : (err.message || (es ? 'No se pudo cargar' : 'Failed to load')));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [period, agentId, policyType, carrier]);

  const money = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(n || 0);
  const typeLabel = (t) => (TYPE_LABELS[t] ? (es ? TYPE_LABELS[t].es : TYPE_LABELS[t].en) : t);
  const MONTHS = es ? ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'] : ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const monthShort = (k) => { const m = parseInt((k || '').split('-')[1], 10); return MONTHS[m - 1] || k; };
  const tooltipStyle = { background: '#0d1c2e', border: '1px solid #1a3550', borderRadius: 8, fontSize: 12 };
  const growthData = (data?.growth || []).map((g) => ({ ...g, name: monthShort(g.month) }));
  const finData = (data?.financial?.byMonth || []).map((f) => ({ ...f, name: monthShort(f.month) }));

  // ── styles ──
  const card = { background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 12, padding: '.85rem' };
  const sel = { background: 'var(--c-surface3,#162d44)', border: '1px solid var(--c-border,#1a3550)', color: 'var(--c-white,#eef6ff)', fontSize: '.75rem', fontWeight: 600, padding: '.4rem .6rem', borderRadius: 8, fontFamily: 'inherit', cursor: 'pointer' };
  const kpiLabel = { fontSize: '.62rem', color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.4px' };
  const kpiVal = { fontSize: '1.35rem', fontWeight: 800, color: 'var(--c-white,#eef6ff)' };
  const th2 = { padding: '.5rem .6rem', fontWeight: 700, fontSize: '.66rem', color: 'var(--c-text3,#45688a)', textTransform: 'uppercase', letterSpacing: '.3px', whiteSpace: 'nowrap' };
  const td2 = { padding: '.45rem .6rem', fontSize: '.76rem', color: 'var(--c-white,#eef6ff)', whiteSpace: 'nowrap' };
  const sectionTitle = { fontSize: '.78rem', fontWeight: 700, color: 'var(--c-white,#eef6ff)', marginBottom: '.5rem' };

  const k = data?.kpis;
  const ki = data?.keyIndicators;

  const Bars = ({ rows, labelKey, color }) => {
    const max = Math.max(1, ...rows.map((r) => r.count));
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '.4rem' }}>
        {rows.map((r, i) => (
          <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '.5rem', fontSize: '.7rem' }}>
            <span style={{ width: 110, color: 'var(--c-text2,#7aa3c0)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{labelKey(r)}</span>
            <span style={{ flex: 1, background: 'var(--c-surface,#0d1c2e)', borderRadius: 4, height: 12 }}>
              <span style={{ display: 'block', height: 12, width: `${Math.round((r.count / max) * 100)}%`, background: color, borderRadius: 4 }} />
            </span>
            <span style={{ width: 28, textAlign: 'right', fontWeight: 700, color: 'var(--c-white,#eef6ff)' }}>{r.count}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '.8rem', flexWrap: 'wrap', gap: '.5rem' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '.5rem' }}>
            <BarChart2 size={20} style={{ color: 'var(--c-cyan,#00d4ff)' }} />
            <h2 style={{ fontWeight: 800, fontSize: '1.1rem', color: 'var(--c-white,#eef6ff)', margin: 0 }}>{es ? 'Métricas y Análisis de Agencia' : 'Agency Metrics & Analytics'}</h2>
          </div>
          <p style={{ fontSize: '.72rem', color: 'var(--c-text3,#45688a)', margin: '.25rem 0 0' }}>{es ? 'Toda la agencia — solo números, sin clientes (respeta el muro)' : 'Whole agency — numbers only, no client data (respects the wall)'}</p>
        </div>
        <button type="button" onClick={load} disabled={loading} style={{ display: 'flex', alignItems: 'center', gap: 5, background: 'var(--c-surface2,#112538)', border: '1px solid var(--c-border,#1a3550)', color: 'var(--c-cyan,#00d4ff)', fontSize: '.75rem', fontWeight: 700, padding: '.45rem .85rem', borderRadius: 9, cursor: loading ? 'default' : 'pointer', opacity: loading ? 0.6 : 1, fontFamily: 'inherit' }}>
          <RefreshCw size={14} /> {es ? 'Actualizar' : 'Refresh'}
        </button>
      </div>

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: '.5rem', flexWrap: 'wrap', marginBottom: '.8rem' }}>
        <select value={period} onChange={(e) => setPeriod(e.target.value)} style={sel}>
          <option value="month">{es ? 'Este mes' : 'This month'}</option>
          <option value="year">{es ? 'Este año' : 'This year'}</option>
          <option value="12m">{es ? 'Últimos 12 meses' : 'Last 12 months'}</option>
          <option value="all">{es ? 'Todo' : 'All time'}</option>
        </select>
        <select value={agentId} onChange={(e) => setAgentId(e.target.value)} style={sel}>
          <option value="">{es ? '👤 Todos los agentes' : '👤 All agents'}</option>
          {(data?.filters?.agents || []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>
        <select value={policyType} onChange={(e) => setPolicyType(e.target.value)} style={sel}>
          <option value="">{es ? '📄 Todos los tipos' : '📄 All types'}</option>
          {(data?.filters?.policyTypes || []).map((t) => <option key={t} value={t}>{typeLabel(t)}</option>)}
        </select>
        <select value={carrier} onChange={(e) => setCarrier(e.target.value)} style={sel}>
          <option value="">{es ? '🏢 Todas las compañías' : '🏢 All carriers'}</option>
          {(data?.filters?.carriers || []).map((cc) => <option key={cc} value={cc}>{cc}</option>)}
        </select>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '.35rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        {[
          { id: 'overview', es: 'Resumen', en: 'Overview', ready: true },
          { id: 'growth', es: 'Crecimiento', en: 'Growth', ready: true },
          { id: 'financial', es: 'Financiero', en: 'Financial', ready: true },
          { id: 'agents', es: 'Agentes', en: 'Agents', ready: true },
        ].map((tb) => (
          <button key={tb.id} type="button" onClick={() => setTab(tb.id)} style={{ background: tab === tb.id ? 'var(--c-cyan,#00d4ff)' : 'var(--c-surface2,#112538)', color: tab === tb.id ? '#06202e' : 'var(--c-text2,#7aa3c0)', border: '1px solid var(--c-border,#1a3550)', borderRadius: 8, padding: '.4rem .8rem', fontSize: '.74rem', fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
            {es ? tb.es : tb.en}{!tb.ready && ' ·'}
          </button>
        ))}
      </div>

      {loading && <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--c-text3,#45688a)', fontSize: '.85rem' }}>{es ? 'Cargando…' : 'Loading…'}</div>}
      {error && !loading && <div style={{ background: 'rgba(239,68,68,.1)', border: '1px solid rgba(239,68,68,.35)', borderRadius: 10, padding: '.75rem 1rem', color: '#fca5a5', fontSize: '.8rem' }}>{error}</div>}

      {/* ── GROWTH ── */}
      {!loading && !error && data && tab === 'growth' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '.8rem' }}>
          <div style={card}>
            <div style={sectionTitle}>{es ? 'Nuevos por mes (últimos 12 meses)' : 'New per month (last 12 months)'}</div>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={growthData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1a3550" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#7aa3c0' }} />
                <YAxis tick={{ fontSize: 11, fill: '#7aa3c0' }} allowDecimals={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="newClients" name={es ? 'Clientes nuevos' : 'New clients'} fill="#00d4ff" radius={[3, 3, 0, 0]} />
                <Bar dataKey="newPolicies" name={es ? 'Pólizas nuevas' : 'New policies'} fill="#a78bfa" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div style={card}>
            <div style={sectionTitle}>{es ? 'Acumulado (crecimiento del libro)' : 'Cumulative (book growth)'}</div>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={growthData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1a3550" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#7aa3c0' }} />
                <YAxis tick={{ fontSize: 11, fill: '#7aa3c0' }} allowDecimals={false} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="cumulativeClients" name={es ? 'Clientes' : 'Clients'} stroke="#00d4ff" strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="cumulativePolicies" name={es ? 'Pólizas' : 'Policies'} stroke="#a78bfa" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ── FINANCIAL ── */}
      {!loading && !error && data && tab === 'financial' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '.8rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '.6rem' }}>
            {[
              { l: es ? 'Comisiones (12m)' : 'Commissions (12mo)', v: money(data.financial.totalCommissions), accent: true },
              { l: es ? 'Gastos (12m)' : 'Expenses (12mo)', v: money(data.financial.totalExpenses) },
              { l: es ? 'Utilidad neta' : 'Net profit', v: money(data.financial.netProfit) },
              { l: es ? 'Cartera anual' : 'Annual book', v: money(data.financial.annualBook) },
            ].map((kpi, i) => (
              <div key={i} style={card}>
                <div style={kpiLabel}>{kpi.l}</div>
                <div style={{ ...kpiVal, color: kpi.accent ? 'var(--c-cyan,#00d4ff)' : kpiVal.color }}>{kpi.v}</div>
              </div>
            ))}
          </div>
          <div style={card}>
            <div style={sectionTitle}>{es ? 'Comisiones vs Gastos por mes' : 'Commissions vs Expenses per month'}</div>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={finData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1a3550" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#7aa3c0' }} />
                <YAxis tick={{ fontSize: 11, fill: '#7aa3c0' }} tickFormatter={(v) => (v >= 1000 ? `$${Math.round(v / 1000)}k` : `$${v}`)} />
                <Tooltip contentStyle={tooltipStyle} formatter={(v) => money(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="commissions" name={es ? 'Comisiones' : 'Commissions'} fill="#34d399" radius={[3, 3, 0, 0]} />
                <Bar dataKey="expenses" name={es ? 'Gastos' : 'Expenses'} fill="#fb923c" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div style={card}>
            <div style={sectionTitle}>{es ? 'Resumen P&L (12 meses)' : 'P&L summary (12 months)'}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '.3rem', fontSize: '.74rem' }}>
              {[
                { l: es ? 'Cartera anual (prima)' : 'Annual book (premium)', v: money(data.financial.annualBook) },
                { l: es ? 'Comisiones recibidas' : 'Commissions received', v: money(data.financial.totalCommissions) },
                { l: es ? 'Gastos operativos' : 'Operating expenses', v: money(data.financial.totalExpenses) },
                { l: es ? 'Utilidad neta' : 'Net profit', v: money(data.financial.netProfit), bold: true },
              ].map((row, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '.35rem .5rem', background: i % 2 ? 'var(--c-surface,#0d1c2e)' : 'transparent', borderRadius: 6 }}>
                  <span style={{ color: 'var(--c-text2,#7aa3c0)' }}>{row.l}</span>
                  <span style={{ fontWeight: row.bold ? 800 : 600, color: row.bold ? 'var(--c-cyan,#00d4ff)' : 'var(--c-white,#eef6ff)' }}>{row.v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── AGENTS ── */}
      {!loading && !error && data && tab === 'agents' && (
        <div style={card}>
          <div style={sectionTitle}>{es ? 'Producción por agente' : 'Production by agent'}</div>
          {!production ? <div style={{ color: 'var(--c-text3,#45688a)', fontSize: '.8rem' }}>{es ? 'Cargando…' : 'Loading…'}</div>
            : production.rows.length === 0 ? <div style={{ color: 'var(--c-text3,#45688a)', fontSize: '.8rem' }}>{es ? 'Sin agentes' : 'No agents'}</div> : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 740 }}>
                  <thead>
                    <tr style={{ background: 'var(--c-surface,#0d1c2e)', textAlign: 'left' }}>
                      <th style={th2}>{es ? 'Agente' : 'Agent'}</th>
                      <th style={{ ...th2, textAlign: 'right' }}>{es ? 'Clientes' : 'Clients'}</th>
                      <th style={{ ...th2, textAlign: 'right' }}>{es ? 'Pólizas' : 'Policies'}</th>
                      <th style={{ ...th2, textAlign: 'right' }}>{es ? 'Prima anual' : 'Annual prem.'}</th>
                      <th style={{ ...th2, textAlign: 'right' }}>{es ? 'Com. mes' : 'Comm. mo.'}</th>
                      <th style={{ ...th2, textAlign: 'right' }}>{es ? 'Com. año' : 'Comm. yr.'}</th>
                      <th style={{ ...th2, textAlign: 'right' }}>{es ? 'Renov. 30d' : 'Renew 30d'}</th>
                      <th style={{ ...th2, textAlign: 'right' }}>{es ? 'Bajas' : 'Churn'}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {production.rows.map((r, i) => (
                      <tr key={r.userId} style={{ borderTop: '1px solid var(--c-border,#1a3550)', background: i % 2 ? 'var(--c-surface,#0d1c2e)' : 'transparent' }}>
                        <td style={{ ...td2, fontWeight: 600 }}>{r.name}</td>
                        <td style={{ ...td2, textAlign: 'right' }}>{r.clientsActive}</td>
                        <td style={{ ...td2, textAlign: 'right' }}>{r.policiesActive}</td>
                        <td style={{ ...td2, textAlign: 'right' }}>{money(r.annualPremium)}</td>
                        <td style={{ ...td2, textAlign: 'right' }}>{money(r.commissionMonth)}</td>
                        <td style={{ ...td2, textAlign: 'right' }}>{money(r.commissionYear)}</td>
                        <td style={{ ...td2, textAlign: 'right' }}>{r.renewals30}</td>
                        <td style={{ ...td2, textAlign: 'right' }}>{r.churn}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
        </div>
      )}

      {!loading && !error && data && tab === 'overview' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '.8rem' }}>
          {/* KPI cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '.6rem' }}>
            {[
              { l: es ? 'Clientes activos' : 'Active clients', v: k.activeClients },
              { l: es ? 'Pólizas activas' : 'Active policies', v: k.activePolicies },
              { l: es ? 'Prima mensual' : 'Monthly premium', v: money(k.monthlyPremium) },
              { l: es ? 'Prima anual' : 'Annual premium', v: money(k.annualPremium), accent: true },
              { l: es ? 'Conversión' : 'Conversion', v: `${k.conversionPct}%`, sub: `${k.leadsConverted}/${k.leadsTotal} leads` },
              { l: es ? 'Renuevan <90d' : 'Renewing <90d', v: k.renewing90 },
            ].map((kpi, i) => (
              <div key={i} style={card}>
                <div style={kpiLabel}>{kpi.l}</div>
                <div style={{ ...kpiVal, color: kpi.accent ? 'var(--c-cyan,#00d4ff)' : kpiVal.color }}>{kpi.v}</div>
                {kpi.sub && <div style={{ fontSize: '.6rem', color: 'var(--c-text3,#45688a)', marginTop: 2 }}>{kpi.sub}</div>}
              </div>
            ))}
          </div>

          {/* Policy mix (donut) + Top carriers (bars) */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '.6rem' }}>
            <div style={card}>
              <div style={{ fontSize: '.78rem', fontWeight: 700, color: 'var(--c-white,#eef6ff)', marginBottom: '.5rem' }}>{es ? 'Pólizas por tipo' : 'Policies by type'}</div>
              {data.policyMix.length === 0 ? <div style={{ fontSize: '.72rem', color: 'var(--c-text3,#45688a)' }}>{es ? 'Sin datos' : 'No data'}</div> : (
                <div style={{ display: 'flex', alignItems: 'center', gap: '.8rem', flexWrap: 'wrap' }}>
                  <div style={{ width: 150, height: 150 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie data={data.policyMix} dataKey="count" nameKey="type" innerRadius={42} outerRadius={70} paddingAngle={2} stroke="none">
                          {data.policyMix.map((e, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                        </Pie>
                        <Tooltip formatter={(v, n) => [v, typeLabel(n)]} contentStyle={{ background: '#0d1c2e', border: '1px solid #1a3550', borderRadius: 8, fontSize: 12 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '.25rem', fontSize: '.68rem' }}>
                    {data.policyMix.slice(0, 6).map((m, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '.4rem', color: 'var(--c-text2,#7aa3c0)' }}>
                        <span style={{ width: 9, height: 9, borderRadius: '50%', background: CHART_COLORS[i % CHART_COLORS.length] }} />
                        {typeLabel(m.type)} <b style={{ color: 'var(--c-white,#eef6ff)' }}>{m.count}</b>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div style={card}>
              <div style={{ fontSize: '.78rem', fontWeight: 700, color: 'var(--c-white,#eef6ff)', marginBottom: '.5rem' }}>{es ? 'Top compañías' : 'Top carriers'}</div>
              {data.topCarriers.length === 0 ? <div style={{ fontSize: '.72rem', color: 'var(--c-text3,#45688a)' }}>{es ? 'Sin datos' : 'No data'}</div>
                : <Bars rows={data.topCarriers} labelKey={(r) => r.carrier} color="#22d3ee" />}
            </div>
          </div>

          {/* Lead sources + Key indicators */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '.6rem' }}>
            <div style={card}>
              <div style={{ fontSize: '.78rem', fontWeight: 700, color: 'var(--c-white,#eef6ff)', marginBottom: '.5rem' }}>{es ? 'Origen de leads' : 'Lead sources'}</div>
              {data.leadSources.length === 0 ? <div style={{ fontSize: '.72rem', color: 'var(--c-text3,#45688a)' }}>{es ? 'Sin datos en el período' : 'No data in period'}</div>
                : <Bars rows={data.leadSources} labelKey={(r) => r.source} color="#34d399" />}
            </div>
            <div style={card}>
              <div style={{ fontSize: '.78rem', fontWeight: 700, color: 'var(--c-white,#eef6ff)', marginBottom: '.5rem' }}>{es ? 'Indicadores clave' : 'Key indicators'}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '.3rem', fontSize: '.72rem' }}>
                {[
                  { l: es ? 'Prima prom. / póliza (mes)' : 'Avg premium / policy (mo.)', v: money(ki.avgMonthlyPremiumPerPolicy) },
                  { l: es ? 'Pólizas por cliente' : 'Policies per client', v: ki.policiesPerClient },
                  { l: es ? 'Salud vs P&C' : 'Health vs P&C', v: `${ki.healthClients} / ${ki.pcClients}` },
                  { l: es ? 'Vida / otros' : 'Life / other', v: ki.lifeClients },
                  // Retención de cohorte: de los clientes que ya tenían póliza
                  // hace un año, cuántos siguen activos. Un guion cuando la
                  // agencia todavía no cumple un año — 0% diría que los perdió
                  // a todos.
                  {
                    l: es ? 'Retención 12m' : 'Retention 12mo',
                    v: ki.retention12mPct == null ? '—' : `${ki.retention12mPct}%`,
                    sub: ki.retention12mPct == null
                      ? (es ? 'aún no hay un año de historia' : 'less than a year of history')
                      : (es ? `de ${ki.retention12mCohort} clientes de hace un año` : `of ${ki.retention12mCohort} clients from a year ago`),
                  },
                ].map((row, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '.35rem .5rem', background: i % 2 ? 'var(--c-surface,#0d1c2e)' : 'transparent', borderRadius: 6 }}>
                    <span style={{ color: 'var(--c-text2,#7aa3c0)' }}>
                      {row.l}
                      {/* La letra chica dice sobre qué se calculó el número. Sin
                          ella, "82%" no se puede juzgar: no se sabe si son 200
                          clientes o 3. */}
                      {row.sub && <span style={{ display: 'block', fontSize: '.62rem', color: 'var(--c-text3,#45688a)' }}>{row.sub}</span>}
                    </span>
                    <span style={{ fontWeight: 800, color: 'var(--c-white,#eef6ff)' }}>{row.v}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '.4rem', fontSize: '.66rem', color: 'var(--c-text3,#45688a)' }}>
            <Shield size={12} style={{ color: 'var(--c-cyan,#00d4ff)' }} />
            {es ? 'Solo dueños/admins ven este módulo. Nunca muestra clientes de los agentes, solo totales.' : 'Owners/admins only. Never shows agents’ clients, only totals.'}
          </div>
        </div>
      )}
    </div>
  );
}
